let ctx = null;
const getCtx = () => {
  if (!ctx) ctx = new (window.AudioContext || window.webkitAudioContext)();
  if (ctx.state === 'suspended') ctx.resume();
  return ctx;
};

// ===== Configuração de áudio (persistida no navegador) =====
const readVol = (key, def) => {
  const v = parseFloat(localStorage.getItem(key));
  return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : def;
};

export const getAudioConfig = () => ({
  muted: localStorage.getItem('oil_muted') === 'on',
  sfxOn: localStorage.getItem('oil_sfx') !== 'off' && localStorage.getItem('oil_sound') !== 'off',
  sfxVol: readVol('oil_sfx_vol', 0.8),
  musicOn: localStorage.getItem('oil_music') !== 'off',
  musicVol: readVol('oil_music_vol', 0.35),
});

export const soundEnabled = () => {
  const c = getAudioConfig();
  return c.sfxOn && !c.muted && c.sfxVol > 0;
};

// ===== Música de fundo =====
let el = null;
let currentSrc = null;
let fadeTimer = null;

const fade = (to, ms, then) => {
  if (!el) return;
  clearInterval(fadeTimer);
  const from = el.volume;
  const steps = Math.max(1, Math.round(ms / 40));
  let i = 0;
  fadeTimer = setInterval(() => {
    i += 1;
    if (!el) { clearInterval(fadeTimer); return; }
    el.volume = Math.min(1, Math.max(0, from + ((to - from) * i) / steps));
    if (i >= steps) { clearInterval(fadeTimer); then?.(); }
  }, 40);
};

export const applyAudioToMusic = () => {
  if (!el) return;
  const c = getAudioConfig();
  el.volume = c.muted ? 0 : c.musicVol;
  if (!c.musicOn || c.muted || c.musicVol === 0) el.pause();
  else if (currentSrc && el.paused) el.play().catch(() => {});
};

export const setAudioConfig = (patch) => {
  if ('muted' in patch) localStorage.setItem('oil_muted', patch.muted ? 'on' : 'off');
  if ('sfxOn' in patch) {
    localStorage.setItem('oil_sfx', patch.sfxOn ? 'on' : 'off');
    localStorage.setItem('oil_sound', patch.sfxOn ? 'on' : 'off'); // compat
  }
  if ('sfxVol' in patch) localStorage.setItem('oil_sfx_vol', String(patch.sfxVol));
  if ('musicOn' in patch) localStorage.setItem('oil_music', patch.musicOn ? 'on' : 'off');
  if ('musicVol' in patch) localStorage.setItem('oil_music_vol', String(patch.musicVol));
  applyAudioToMusic();
  return getAudioConfig();
};

export const music = {
  play: (src) => {
    if (!el) {
      el = new Audio();
      el.loop = true;
      el.preload = 'auto';
    }
    const c = getAudioConfig();
    const vol = c.muted ? 0 : c.musicVol;
    if (!c.musicOn || c.muted || c.musicVol === 0) { el.pause(); currentSrc = src; return; }
    if (currentSrc !== src) {
      const swap = () => {
        el.src = src;
        currentSrc = src;
        el.volume = 0;
        const p = el.play();
        if (p && p.catch) {
          p.catch(() => {
            const retry = () => { if (el) el.play().catch(() => {}); };
            document.addEventListener('pointerdown', retry, { once: true });
          });
        }
        fade(vol, 400); // entra suave
      };
      if (currentSrc && !el.paused) fade(0, 250, swap); // sai suave
      else swap();
      return;
    }
    el.volume = vol;
    const p = el.play();
    if (p && p.catch) {
      p.catch(() => {
        const retry = () => { if (el) el.play().catch(() => {}); };
        document.addEventListener('pointerdown', retry, { once: true });
      });
    }
  },
  stop: () => {
    if (!el) return;
    el.pause();
    el.currentTime = 0;
    currentSrc = null; // fora da aba MÁFIA a música não pode voltar sozinha
  },
  duck: (on) => {
    if (!el) return;
    const c = getAudioConfig();
    el.volume = c.muted ? 0 : (on ? c.musicVol * 0.12 : c.musicVol);
  },
};

// ===== Efeitos sonoros gravados (arquivos) =====
const clipCache = {};
export const playClip = (src, vol = 1) => {
  const c = getAudioConfig();
  if (!c.sfxOn || c.muted || c.sfxVol <= 0) return;
  try {
    let a = clipCache[src];
    if (!a) { a = new Audio(src); a.preload = 'auto'; clipCache[src] = a; }
    a.currentTime = 0;
    a.volume = Math.min(1, c.sfxVol * vol);
    a.play().catch(() => {});
  } catch {}
};

// clipe com corte suave: toca `dur` ms e some no último trecho
export const playClipFade = (src, dur = 3500, fade = 1200, vol = 1) => {
  const c = getAudioConfig();
  if (!c.sfxOn || c.muted || c.sfxVol <= 0) return;
  const a = new Audio(src);
  const peak = Math.min(1, c.sfxVol * vol);
  a.volume = peak;
  a.play().catch(() => {});
  const t0 = performance.now();
  const tick = () => {
    const left = dur - (performance.now() - t0);
    if (left <= 0) { a.pause(); a.src = ''; return; }
    a.volume = left < fade ? peak * (left / fade) : peak;
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
};

// abrir card/painel e fechar/alternar
export const sfxOpen = () => playClip('/oil/sfx_procurados.mp3');
export const sfxClose = () => playClip('/oil/sfx_missao.mp3');
export const sfxTab = () => playClip('/oil/sfx_missao.mp3', 0.5);

// ===== Efeitos sonoros =====
let master = null;
const getMaster = (ac) => {
  if (!master || master.context !== ac) {
    master = ac.createGain();
    master.connect(ac.destination);
  }
  master.gain.value = getAudioConfig().sfxVol;
  return master;
};

const tone = (ac, { freq = 440, type = 'sine', t0 = 0, dur = 0.2, vol = 0.15, slideTo = null }) => {
  const o = ac.createOscillator();
  const g = ac.createGain();
  o.type = type;
  o.frequency.setValueAtTime(freq, ac.currentTime + t0);
  if (slideTo) o.frequency.exponentialRampToValueAtTime(slideTo, ac.currentTime + t0 + dur);
  g.gain.setValueAtTime(vol, ac.currentTime + t0);
  g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + t0 + dur);
  o.connect(g).connect(getMaster(ac));
  o.start(ac.currentTime + t0);
  o.stop(ac.currentTime + t0 + dur + 0.05);
};

const noise = (ac, { t0 = 0, dur = 0.4, vol = 0.12, filterFreq = 400 }) => {
  const len = Math.ceil(ac.sampleRate * dur);
  const buf = ac.createBuffer(1, len, ac.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  const src = ac.createBufferSource();
  src.buffer = buf;
  const f = ac.createBiquadFilter();
  f.type = 'lowpass';
  f.frequency.value = filterFreq;
  const g = ac.createGain();
  g.gain.setValueAtTime(vol, ac.currentTime + t0);
  g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + t0 + dur);
  src.connect(f).connect(g).connect(getMaster(ac));
  src.start(ac.currentTime + t0);
};

const safe = (fn) => { if (!soundEnabled()) return; try { fn(getCtx()); } catch {} };

export const sfx = {
  // clique/feedback de ajuste (usado nas configurações)
  tick: () => safe((ac) => tone(ac, { freq: 880, type: 'triangle', dur: 0.07, vol: 0.1 })),
  // perfuração: motor grave + ruído de broca
  drill: () => safe((ac) => {
    noise(ac, { dur: 1.3, vol: 0.1, filterFreq: 300 });
    tone(ac, { freq: 55, type: 'sawtooth', dur: 1.3, vol: 0.08, slideTo: 75 });
    for (let i = 0; i < 6; i++) tone(ac, { freq: 140 + i * 8, type: 'square', t0: i * 0.2, dur: 0.08, vol: 0.04 });
  }),
  // jorro de petróleo (sucesso na extração)
  gush: () => safe((ac) => {
    noise(ac, { dur: 0.5, vol: 0.12, filterFreq: 1200 });
    tone(ac, { freq: 300, type: 'sine', dur: 0.5, vol: 0.1, slideTo: 900 });
  }),
  // poço seco
  dry: () => safe((ac) => tone(ac, { freq: 200, type: 'triangle', dur: 0.5, vol: 0.12, slideTo: 90 })),
  // venda: caixa registradora (arpejo + sino)
  cash: () => safe((ac) => {
    [523, 659, 784, 1047].forEach((f, i) => tone(ac, { freq: f, type: 'triangle', t0: i * 0.07, dur: 0.18, vol: 0.12 }));
    tone(ac, { freq: 1568, type: 'sine', t0: 0.3, dur: 0.35, vol: 0.1 });
  }),
  // construção/upgrade: marteladas + acorde
  build: () => safe((ac) => {
    noise(ac, { dur: 0.1, vol: 0.15, filterFreq: 800 });
    noise(ac, { t0: 0.18, dur: 0.1, vol: 0.15, filterFreq: 800 });
    [392, 494, 587].forEach((f, i) => tone(ac, { freq: f, type: 'triangle', t0: 0.35 + i * 0.05, dur: 0.3, vol: 0.1 }));
  }),
  // sirene de ataque (ofensiva executada / você atacou)
  siren: () => safe((ac) => {
    tone(ac, { freq: 600, type: 'sawtooth', dur: 0.4, vol: 0.09, slideTo: 950 });
    tone(ac, { freq: 950, type: 'sawtooth', t0: 0.4, dur: 0.4, vol: 0.09, slideTo: 600 });
    tone(ac, { freq: 600, type: 'sawtooth', t0: 0.8, dur: 0.35, vol: 0.07, slideTo: 950 });
  }),
  // escudo ativado: "vidro blindado"
  shield: () => safe((ac) => {
    tone(ac, { freq: 880, type: 'sine', dur: 0.12, vol: 0.12 });
    tone(ac, { freq: 1320, type: 'sine', t0: 0.1, dur: 0.25, vol: 0.1 });
    tone(ac, { freq: 220, type: 'triangle', t0: 0.05, dur: 0.3, vol: 0.08 });
  }),
  // golpe bloqueado (metal)
  blocked: () => safe((ac) => {
    noise(ac, { dur: 0.15, vol: 0.15, filterFreq: 2500 });
    tone(ac, { freq: 1200, type: 'square', dur: 0.2, vol: 0.06, slideTo: 400 });
  }),
  // tilim curto de moeda (jackpot subiu)
  coin: () => safe((ac) => {
    tone(ac, { freq: 1568, type: 'triangle', dur: 0.08, vol: 0.09 });
    tone(ac, { freq: 2349, type: 'triangle', t0: 0.06, dur: 0.16, vol: 0.07 });
  }),
  // falha genérica
  fail: () => safe((ac) => {
    tone(ac, { freq: 260, type: 'square', dur: 0.15, vol: 0.08 });
    tone(ac, { freq: 180, type: 'square', t0: 0.16, dur: 0.3, vol: 0.08 });
  }),
};
